import { get } from 'lodash';
import { momentFromApiDate } from 'Utils/date/apiDate';
import type { OverloadForSpecificProposalIdsType } from 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
const DATE_FORMAT_BY_LOCALE = 'D MMMM YYYY';
export const TRANSPORT_TYPES = {
  BOAT: 'BOAT',
  PLANE: 'PLANE',
  TRAIN: 'TRAIN',
};

function getTotalDifferentialPrice(price: FormattedPrice): string {
  return `${price.value > 0 ? '+' : ''}${price.formattedValue}`;
}

function getTransportAverageDuration(transport: AlternativeTransport | null | undefined): number {
  return transport
    ? Math.round(
        (Number(transport?.inbound.durationInMin) + Number(transport?.outbound.durationInMin)) / 2,
      )
    : 0;
}

function getHasStrictTransportOrder(
  transports: Array<AlternativeTransport>,
  selectedOrder: 'cheapest' | 'fastest',
): boolean {
  const firstTransport = transports[0];
  const secondTransport = transports[1];

  if (!firstTransport || !secondTransport) {
    return false;
  }

  if (selectedOrder === 'cheapest') {
    return firstTransport.price.value !== secondTransport.price.value;
  }

  const firstTransportDuration = getTransportAverageDuration(firstTransport);
  const secondTransportDuration = getTransportAverageDuration(secondTransport);
  return firstTransportDuration !== secondTransportDuration;
}

function orderTransportByDuration(
  transports: Array<AlternativeTransport>,
): Array<AlternativeTransport> {
  return transports.sort((a, b) => getTransportAverageDuration(a) - getTransportAverageDuration(b));
}

function orderTransportByPrice(
  transports: Array<AlternativeTransport>,
): Array<AlternativeTransport> {
  return transports.sort((a, b) => a.price.value - b.price.value);
}

function orderTransportByType(
  transports: Array<AlternativeTransport>,
  selectedOrder: 'cheapest' | 'fastest',
): Array<AlternativeTransport> {
  return selectedOrder === 'cheapest'
    ? orderTransportByPrice(transports)
    : orderTransportByDuration(transports);
}

function getOutboundDeparture(
  transport: Transport | null | undefined,
): TravelSectionBound | null | undefined {
  return get(transport, 'outbound.travelSections[0].departure');
}

function getInboundDeparture(
  transport: Transport | null | undefined,
): TravelSectionBound | null | undefined {
  return get(transport, 'inbound.travelSections[0].departure');
}

function getTravelSectionBoundSummary(travelBound: TravelSectionBound | null | undefined): string {
  const sectionBoundCityLabel = get(travelBound, 'label');
  const sectionBoundDate = get(travelBound, 'date');
  const formattedDate =
    sectionBoundDate &&
    momentFromApiDate(get(travelBound, 'date', '')).format(DATE_FORMAT_BY_LOCALE);
  return sectionBoundCityLabel && formattedDate && `${sectionBoundCityLabel}, ${formattedDate}`;
}

const getProposalIdsAndOverloadForAdditionalServiceQuery = (
  proposals: ReadonlyArray<Proposal>,
  selectedTransfer: UpsellTransfer,
): {
  proposalIds: ReadonlyArray<string>;
  overloadForSpecificProposalIds?: OverloadForSpecificProposalIdsType;
} => {
  let chaletTransfer;
  const proposalIdsToExclude = [];
  proposals.forEach(({ id, additionalServices }) => {
    const differentTransfer = additionalServices.transfers.find(
      ({ productInfo }) => productInfo.label === selectedTransfer.productInfo.label,
    );

    if (differentTransfer) {
      if (differentTransfer.pricingInfo.id !== selectedTransfer.pricingInfo.id) {
        chaletTransfer = {
          proposalIds: [...(chaletTransfer?.proposalIds || []), id],
          serviceId: differentTransfer.pricingInfo.id,
        };
      }
    } else {
      proposalIdsToExclude.push(id);
    }
  });
  return {
    proposalIds: proposals.map(({ id }) => id).filter((id) => !proposalIdsToExclude.includes(id)),
    overloadForSpecificProposalIds: chaletTransfer
      ? {
          proposalIds: chaletTransfer.proposalIds,
          serviceIds: [chaletTransfer.serviceId],
        }
      : undefined,
  };
};

function isTransferIncludedInProposal(proposal: Proposal): boolean {
  const transfers = proposal.additionalServices?.transfers ?? [];
  const allTransfersAreIncluded =
    transfers.length > 0 && transfers.every((transfer) => transfer.pricingInfo.isIncluded);

  return (
    !!proposal.transports?.inbound?.transferIncluded ||
    !!proposal.transports?.outbound?.transferIncluded ||
    allTransfersAreIncluded
  );
}

const computeTransportDetailEnriched = (transportDetail: TransportDetail) => {
  const firstSection = transportDetail.travelSections[0];
  const [lastSection] = transportDetail.travelSections.slice(-1);
  return {
    ...transportDetail,
    departure: firstSection.departure,
    arrival: lastSection.arrival,
    transport: firstSection.transport,
    stops: transportDetail.travelSections.length - 1,
  };
};

export {
  getTotalDifferentialPrice,
  getTransportAverageDuration,
  getHasStrictTransportOrder,
  orderTransportByDuration,
  orderTransportByPrice,
  orderTransportByType,
  getInboundDeparture,
  getOutboundDeparture,
  getProposalIdsAndOverloadForAdditionalServiceQuery,
  getTravelSectionBoundSummary,
  computeTransportDetailEnriched,
  isTransferIncludedInProposal,
};
